<template>
	<div class="lv" :class="['is-' + kind, { 'is-compact': compact }]" aria-hidden="true">
		<div v-if="kind === 'hero'" class="lv-hero">
			<div class="lv-glow lv-glow-a"></div>
			<div class="lv-glow lv-glow-b"></div>
			<div class="lv-window">
				<div class="lv-chrome">
					<div class="lv-dots">
						<span></span><span></span><span></span>
					</div>
					<div class="lv-pair">
						<span class="lv-pair-name">{{ pair }}</span>
						<span class="lv-pair-chg" :class="changeUp ? 'is-up' : 'is-down'">{{ changeText }}</span>
					</div>
					<div class="lv-chrome-tabs">
						<span class="is-on">{{ $t('Market.chart') }}</span>
						<span>{{ $t('Market.contracts') }}</span>
					</div>
				</div>
				<div class="lv-hero-body">
					<div class="lv-chart">
						<svg class="lv-candles" viewBox="0 0 280 140" preserveAspectRatio="none">
							<line v-for="y in gridYs" :key="'g-' + y" x1="0" :y1="y" x2="280" :y2="y" class="lv-gridline" />
							<path class="lv-area" d="M18 92 L34 78 L50 64 L66 72 L82 54 L98 46 L114 68 L130 50 L146 36 L162 58 L178 42 L194 30 L210 48 L226 34 L242 26 L258 22 L258 140 L18 140 Z" />
							<g v-for="(c, i) in candles" :key="'c-' + i" :class="c.up ? 'is-up' : 'is-down'">
								<line :x1="c.x" :y1="c.h" :x2="c.x" :y2="c.l" />
								<rect :x="c.x - 4" :y="Math.min(c.o, c.c)" width="8" :height="Math.max(4, Math.abs(c.c - c.o))" rx="1" />
							</g>
						</svg>
						<div class="lv-hero-actions">
							<div class="lv-side-btn is-buy">{{ $t('Market.buy') }}</div>
							<div class="lv-side-btn is-sell">{{ $t('Market.sell') }}</div>
						</div>
					</div>
					<div class="lv-book">
						<div class="lv-book-row is-down" v-for="row in bookAsks" :key="'a-' + row.p">
							<span>{{ row.p }}</span>
							<i :style="{ width: row.w }"></i>
						</div>
						<div class="lv-book-mid">{{ pairPrice }}</div>
						<div class="lv-book-row is-up" v-for="row in bookBids" :key="'b-' + row.p">
							<span>{{ row.p }}</span>
							<i :style="{ width: row.w }"></i>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div v-else-if="kind === 'options'" class="lv-panel">
			<div class="lv-panel-bar">
				<span class="lv-chip is-on">{{ $t('Market.options') }}</span>
				<span class="lv-chip">{{ $t('Market.buy') }}</span>
				<span class="lv-chip">{{ $t('Market.sell') }}</span>
			</div>
			<svg class="lv-payoff" viewBox="0 0 220 110" preserveAspectRatio="none">
				<line x1="12" y1="62" x2="208" y2="62" class="lv-axis" />
				<path class="lv-payoff-fill" d="M16 78 L92 78 L168 28 L208 28 L208 110 L16 110 Z" />
				<path class="lv-payoff-line" d="M16 78 L92 78 L168 28 L208 28" pathLength="1" />
				<circle cx="92" cy="78" r="3.5" class="lv-dot" />
			</svg>
			<div class="lv-meta">
				<span>BTC</span>
				<span class="is-up">+12.4%</span>
			</div>
		</div>

		<div v-else-if="kind === 'contracts'" class="lv-panel">
			<div class="lv-panel-bar">
				<span class="lv-chip is-on">{{ $t('Market.contracts') }}</span>
				<span class="lv-chip">{{ $t('Market.leverage') }} 10x</span>
			</div>
			<svg class="lv-spark" viewBox="0 0 220 90" preserveAspectRatio="none">
				<path class="lv-spark-fill" d="M8 62 C28 58 40 70 58 48 C76 28 90 40 110 30 C132 18 148 36 168 22 C186 12 200 18 212 14 L212 90 L8 90 Z" />
				<path class="lv-spark-line" d="M8 62 C28 58 40 70 58 48 C76 28 90 40 110 30 C132 18 148 36 168 22 C186 12 200 18 212 14" pathLength="1" />
			</svg>
			<div class="lv-hero-actions lv-mini-actions">
				<div class="lv-side-btn is-buy">{{ $t('Market.buy') }}</div>
				<div class="lv-side-btn is-sell">{{ $t('Market.sell') }}</div>
			</div>
		</div>

		<div v-else-if="kind === 'crypto' || kind === 'forex' || kind === 'stocks'" class="lv-list">
			<div class="lv-list-row" v-for="(row, idx) in listRows" :key="row.s" :style="{ '--lv-i': idx }">
				<div class="lv-asset">
					<span class="lv-mark" :class="row.tone">{{ row.mark }}</span>
					<span class="lv-sym">{{ row.s }}</span>
				</div>
				<svg class="lv-mini" viewBox="0 0 64 24" preserveAspectRatio="none">
					<path class="lv-mini-fill" :class="row.up ? 'is-up' : 'is-down'" :d="miniFill(row.path)" />
					<path class="lv-mini-line" :class="row.up ? 'is-up' : 'is-down'" :d="row.path" pathLength="1" />
				</svg>
				<div class="lv-quote">
					<span class="lv-px">{{ row.p }}</span>
					<span class="lv-chg" :class="row.up ? 'is-up' : 'is-down'">{{ row.c }}</span>
				</div>
			</div>
		</div>

		<div v-else-if="kind === 'lend' || kind === 'deposit'" class="lv-lend">
			<div class="lv-lend-row" v-for="row in lendRows" :key="row.s">
				<div class="lv-lend-top">
					<span>{{ row.s }}</span>
					<span class="is-up">{{ row.apy }}</span>
				</div>
				<div class="lv-bar">
					<i :style="{ width: row.w }"></i>
				</div>
			</div>
		</div>

		<div v-else-if="kind === 'smart'" class="lv-list">
			<div class="lv-panel-bar" style="margin-bottom: 8px">
				<span class="lv-chip is-on">{{ $t('lendingMenu.smartTrading') }}</span>
			</div>
			<div class="lv-list-row" v-for="(row, idx) in smartRows" :key="row.s" :style="{ '--lv-i': idx }">
				<div class="lv-asset">
					<span class="lv-mark" :class="row.tone">{{ row.mark }}</span>
					<span class="lv-sym">{{ row.s }}</span>
				</div>
				<svg class="lv-mini" viewBox="0 0 64 24" preserveAspectRatio="none">
					<path class="lv-mini-fill" :class="row.up ? 'is-up' : 'is-down'" :d="miniFill(row.path)" />
					<path class="lv-mini-line" :class="row.up ? 'is-up' : 'is-down'" :d="row.path" pathLength="1" />
				</svg>
				<div class="lv-quote">
					<span class="lv-px">{{ row.p }}</span>
					<span class="lv-chg" :class="row.up ? 'is-up' : 'is-down'">{{ row.c }}</span>
				</div>
			</div>
		</div>

		<div v-else-if="kind === 'withdraw'" class="lv-list">
			<div class="lv-list-row" v-for="(row, idx) in withdrawRows" :key="row.s" :style="{ '--lv-i': idx }">
				<div class="lv-asset">
					<span class="lv-mark" :class="row.tone">{{ row.mark }}</span>
					<span class="lv-sym">{{ row.s }}</span>
				</div>
				<div class="lv-quote">
					<span>{{ row.p }}</span>
					<span :class="row.up ? 'is-up' : 'is-down'">{{ row.c }}</span>
				</div>
			</div>
		</div>

		<div v-else-if="kind === 'orders'" class="lv-list">
			<div class="lv-list-row" v-for="(row, idx) in orderRows" :key="row.s" :style="{ '--lv-i': idx }">
				<div class="lv-asset">
					<span class="lv-mark" :class="row.tone">{{ row.mark }}</span>
					<span class="lv-sym">{{ row.s }}</span>
				</div>
				<div class="lv-quote">
					<span>{{ row.p }}</span>
					<span :class="row.up ? 'is-up' : 'is-down'">{{ row.c }}</span>
				</div>
			</div>
		</div>

		<div v-else-if="kind === 'invite'" class="lv-lend">
			<div class="lv-lend-row" v-for="row in inviteRows" :key="row.s">
				<div class="lv-lend-top">
					<span>{{ row.s }}</span>
					<span class="is-up">{{ row.apy }}</span>
				</div>
				<div class="lv-bar">
					<i :style="{ width: row.w }"></i>
				</div>
			</div>
		</div>

		<div v-else-if="kind === 'security'" class="lv-sec">
			<div class="lv-sec-mark">
				<svg width="36" height="36" viewBox="0 0 20 20" fill="none">
					<path d="M10 2.5L15.5 5v4.2c0 3.5-2.3 6.6-5.5 7.8-3.2-1.2-5.5-4.3-5.5-7.8V5L10 2.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
					<path d="M8 10.2l1.4 1.4L12.4 8.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
			</div>
			<div class="lv-sec-row" v-for="key in secKeys" :key="key">
				<span class="lv-check">
					<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
						<path d="M2.5 6.2l2.2 2.2 4.8-5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</span>
				<span>{{ $t(key) }}</span>
			</div>
		</div>
	</div>
</template>

<script>
const CANDLES = [
	{ x: 18, up: false, h: 68, l: 102, o: 92, c: 78 },
	{ x: 34, up: true, h: 58, l: 96, o: 86, c: 70 },
	{ x: 50, up: true, h: 44, l: 82, o: 76, c: 54 },
	{ x: 66, up: false, h: 52, l: 88, o: 60, c: 78 },
	{ x: 82, up: true, h: 38, l: 74, o: 70, c: 46 },
	{ x: 98, up: true, h: 32, l: 64, o: 58, c: 40 },
	{ x: 114, up: false, h: 48, l: 86, o: 52, c: 74 },
	{ x: 130, up: true, h: 36, l: 70, o: 66, c: 44 },
	{ x: 146, up: true, h: 22, l: 54, o: 50, c: 30 },
	{ x: 162, up: false, h: 40, l: 76, o: 46, c: 68 },
	{ x: 178, up: true, h: 28, l: 60, o: 56, c: 36 },
	{ x: 194, up: true, h: 18, l: 48, o: 44, c: 24 },
	{ x: 210, up: false, h: 34, l: 66, o: 38, c: 58 },
	{ x: 226, up: true, h: 22, l: 52, o: 48, c: 28 },
	{ x: 242, up: true, h: 16, l: 42, o: 38, c: 22 },
	{ x: 258, up: true, h: 12, l: 36, o: 30, c: 16 }
]

const LISTS = {
	crypto: [
		{ s: 'BTC', mark: 'B', tone: 't-a', p: '67,420', c: '+2.14%', up: true, path: 'M1 14 C10 13 16 8 24 9 C32 10 38 5 55 4' },
		{ s: 'ETH', mark: 'E', tone: 't-b', p: '3,412', c: '+1.08%', up: true, path: 'M1 12 C12 14 18 8 28 9 C38 10 44 6 55 5' },
		{ s: 'SOL', mark: 'S', tone: 't-c', p: '178.40', c: '−0.62%', up: false, path: 'M1 6 C12 7 20 12 30 10 C40 8 46 15 55 16' }
	],
	forex: [
		{ s: 'EUR/USD', mark: '€', tone: 't-a', p: '1.0842', c: '+0.21%', up: true, path: 'M1 13 C14 12 20 8 32 9 C42 10 48 6 55 5' },
		{ s: 'XAU/USD', mark: 'X', tone: 't-b', p: '2,418', c: '+0.54%', up: true, path: 'M1 15 C10 12 22 7 34 8 C44 9 48 5 55 4' },
		{ s: 'GBP/USD', mark: '£', tone: 't-c', p: '1.2710', c: '−0.18%', up: false, path: 'M1 7 C14 8 22 13 34 11 C44 9 48 15 55 16' }
	],
	stocks: [
		{ s: 'AAPL', mark: 'A', tone: 't-a', p: '228.40', c: '+0.86%', up: true, path: 'M1 14 C12 13 18 9 28 8 C40 7 46 5 55 4' },
		{ s: 'TSLA', mark: 'T', tone: 't-b', p: '248.10', c: '−1.22%', up: false, path: 'M1 5 C12 7 20 14 32 12 C42 10 48 16 55 17' },
		{ s: 'NVDA', mark: 'N', tone: 't-c', p: '118.60', c: '+2.05%', up: true, path: 'M1 15 C10 14 18 8 30 9 C40 10 46 5 55 3' }
	]
}

export default {
	name: 'LandingVisual',
	props: {
		kind: {
			type: String,
			required: true
		},
		pair: {
			type: String,
			default: 'BTC/USDT'
		},
		change: {
			type: String,
			default: ''
		},
		changeUp: {
			type: Boolean,
			default: true
		},
		price: {
			type: String,
			default: ''
		},
		compact: {
			type: Boolean,
			default: false
		}
	},
	data: function () {
		return {
			candles: CANDLES,
			gridYs: [28, 56, 84, 112],
			bookAsks: [
				{ p: '67,432.1', w: '72%' },
				{ p: '67,428.6', w: '48%' },
				{ p: '67,421.0', w: '30%' }
			],
			bookBids: [
				{ p: '67,418.2', w: '40%' },
				{ p: '67,412.5', w: '64%' },
				{ p: '67,405.8', w: '28%' }
			],
			lendRows: [
				{ s: 'USDT', apy: '4.20%', w: '72%' },
				{ s: 'USDC', apy: '3.80%', w: '58%' },
				{ s: 'ETH', apy: '2.10%', w: '36%' }
			],
			smartRows: [
				{ s: 'BTC', mark: 'B', tone: 't-a', p: 'Plan', c: '+3.10%', up: true, path: 'M1 14 C12 13 18 8 30 9 C40 10 46 5 55 4' },
				{ s: 'ETH', mark: 'E', tone: 't-b', p: 'Plan', c: '+1.40%', up: true, path: 'M1 12 C14 11 22 7 34 8 C44 9 48 6 55 5' },
				{ s: 'XAU', mark: 'X', tone: 't-c', p: 'Plan', c: '−0.40%', up: false, path: 'M1 6 C14 8 22 13 34 11 C44 9 48 15 55 16' }
			],
			withdrawRows: [
				{ s: 'USDT', mark: 'U', tone: 't-a', p: '2,480', c: '−200', up: false },
				{ s: 'USDC', mark: 'C', tone: 't-b', p: '860', c: '−80', up: false },
				{ s: 'ETH', mark: 'E', tone: 't-c', p: '1.20', c: '−0.10', up: false }
			],
			orderRows: [
				{ s: 'BTC/USDT', mark: 'B', tone: 't-a', p: '0.24', c: '+1.80%', up: true },
				{ s: 'EUR/USD', mark: '€', tone: 't-b', p: '1.20', c: '+0.16%', up: true },
				{ s: 'AAPL', mark: 'A', tone: 't-c', p: '12', c: '−0.40%', up: false }
			],
			inviteRows: [
				{ s: 'A-18', apy: '+12', w: '80%' },
				{ s: 'B-07', apy: '+8', w: '54%' },
				{ s: 'C-03', apy: '+3', w: '36%' }
			],
			secKeys: [
				'landing.secAuditTitle',
				'landing.secIsolateTitle',
				'landing.secTransparentTitle',
				'landing.secSupportTitle'
			]
		}
	},
	computed: {
		listRows: function () {
			return LISTS[this.kind] || LISTS.crypto
		},
		changeText: function () {
			return this.change || '+2.14%'
		},
		pairPrice: function () {
			return this.price || '67,420.12'
		}
	},
	methods: {
		miniFill: function (path) {
			return path + ' L55 24 L1 24 Z'
		}
	}
}
</script>

<style lang="scss" scoped>
.lv {
	width: 100%;
	height: 100%;
	min-height: inherit;
}

.lv-hero {
	position: relative;
	height: 100%;
	min-width: 0;
	border-radius: 20px;
	overflow: hidden;
	isolation: isolate;
}

.lv-glow {
	position: absolute;
	border-radius: 50%;
	pointer-events: none;
	background: radial-gradient(circle, var(--ld-accent, #5347e0) 0%, transparent 70%);
}

.lv-glow-a {
	width: 58%;
	height: 58%;
	right: -6%;
	top: -10%;
	opacity: 0.22;
	animation: lv-glow 7s ease-in-out infinite;
}

.lv-glow-b {
	width: 42%;
	height: 42%;
	left: -8%;
	bottom: 4%;
	opacity: 0.12;
	animation: lv-glow 9s ease-in-out infinite reverse;
}

.lv-window {
	position: relative;
	z-index: 1;
	height: 100%;
	border: 1px solid var(--ld-border, #ececec);
	border-radius: 20px;
	background: var(--ld-bg-card, #fff);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	box-shadow: var(--ld-shadow, 0 16px 48px rgba(0, 0, 0, 0.12));
}

.lv-chrome {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 14px;
	border-bottom: 1px solid var(--ld-border, #ececec);
	background: var(--ld-bg-muted, #f7f7f7);
	min-width: 0;
}

.lv-dots {
	display: flex;
	gap: 5px;

	span {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: var(--ld-border-strong, #d0d0d0);
	}
}

.lv-pair {
	display: flex;
	align-items: baseline;
	gap: 8px;
	min-width: 0;
}

.lv-pair-name {
	font-size: 13px;
	font-weight: 700;
	letter-spacing: -0.02em;
}

.lv-pair-chg {
	font-size: 12px;
	font-weight: 700;
	font-variant-numeric: tabular-nums;
}

.lv-chrome-tabs {
	margin-left: auto;
	display: flex;
	gap: 8px;
	font-size: 11px;
	font-weight: 600;
	color: var(--ld-text-tertiary, #999);

	.is-on {
		color: var(--ld-text, #111);
	}
}

.lv-hero-body {
	flex: 1;
	display: grid;
	grid-template-columns: minmax(0, 1fr) 118px;
	min-width: 0;
	min-height: 0;
}

.lv-chart {
	position: relative;
	padding: 10px 8px 12px 12px;
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.lv-candles {
	display: block;
	width: 100%;
	flex: 1;
	min-width: 0;
	min-height: 120px;
}

.lv-gridline {
	stroke: var(--ld-border, #ececec);
	stroke-width: 1;
}

.lv-area {
	fill: var(--ld-accent-muted, #eef0ff);
	animation: lv-fade-in 0.9s ease both;
}

.lv-candles .is-up line,
.lv-candles .is-up rect {
	stroke: var(--ld-success, #16a34a);
	fill: var(--ld-success, #16a34a);
}

.lv-candles .is-down line,
.lv-candles .is-down rect {
	stroke: var(--ld-danger, #ef4444);
	fill: var(--ld-danger, #ef4444);
}

.lv-candles line {
	stroke-width: 1.4;
}

.lv-candles g {
	transform-box: fill-box;
	transform-origin: center bottom;
	animation: lv-candle 0.55s ease both;
}

.lv-candles g:nth-of-type(1) { animation-delay: 0.04s; }
.lv-candles g:nth-of-type(2) { animation-delay: 0.08s; }
.lv-candles g:nth-of-type(3) { animation-delay: 0.12s; }
.lv-candles g:nth-of-type(4) { animation-delay: 0.16s; }
.lv-candles g:nth-of-type(5) { animation-delay: 0.2s; }
.lv-candles g:nth-of-type(6) { animation-delay: 0.24s; }
.lv-candles g:nth-of-type(7) { animation-delay: 0.28s; }
.lv-candles g:nth-of-type(8) { animation-delay: 0.32s; }
.lv-candles g:nth-of-type(9) { animation-delay: 0.36s; }
.lv-candles g:nth-of-type(10) { animation-delay: 0.4s; }
.lv-candles g:nth-of-type(11) { animation-delay: 0.44s; }
.lv-candles g:nth-of-type(12) { animation-delay: 0.48s; }
.lv-candles g:nth-of-type(13) { animation-delay: 0.52s; }
.lv-candles g:nth-of-type(14) { animation-delay: 0.56s; }
.lv-candles g:nth-of-type(15) { animation-delay: 0.6s; }
.lv-candles g:nth-of-type(16) { animation-delay: 0.64s; }

.lv-hero-actions {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
	margin-top: 10px;
}

.lv-side-btn {
	height: 34px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 700;
	color: #fff;
}

.lv-side-btn.is-buy {
	background: var(--ld-success, #16a34a);
}

.lv-side-btn.is-sell {
	background: var(--ld-danger, #ef4444);
}

.lv-book {
	padding: 12px 12px 12px 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 6px;
}

.lv-book-row {
	position: relative;
	font-size: 10px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	padding: 4px 6px;
	border-radius: 6px;
	overflow: hidden;

	span {
		position: relative;
		z-index: 1;
	}

	i {
		position: absolute;
		right: 0;
		top: 0;
		bottom: 0;
		opacity: 0.16;
		transform-origin: right center;
		animation: lv-bar 0.7s ease both;
	}

	&.is-up {
		color: var(--ld-success, #16a34a);

		i { background: var(--ld-success, #16a34a); }
	}

	&.is-down {
		color: var(--ld-danger, #ef4444);

		i { background: var(--ld-danger, #ef4444); }
	}

	&:nth-child(1) i { animation-delay: 0.08s; }
	&:nth-child(2) i { animation-delay: 0.16s; }
	&:nth-child(3) i { animation-delay: 0.24s; }
	&:nth-child(5) i { animation-delay: 0.32s; }
	&:nth-child(6) i { animation-delay: 0.4s; }
	&:nth-child(7) i { animation-delay: 0.48s; }
}

.lv-book-mid {
	font-size: 13px;
	font-weight: 700;
	letter-spacing: -0.03em;
	font-variant-numeric: tabular-nums;
	padding: 4px 6px;
}

.lv-panel,
.lv-list,
.lv-lend,
.lv-sec {
	height: 100%;
	padding: 12px;
	box-sizing: border-box;
	background:
		radial-gradient(120% 80% at 100% 0%, var(--ld-accent-muted, #eef0ff) 0%, transparent 56%),
		var(--ld-bg-muted, #f7f7f7);
}

.lv-panel {
	display: flex;
	flex-direction: column;
}

.lv-panel-bar {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.lv-chip {
	height: 22px;
	padding: 0 8px;
	border-radius: 999px;
	border: 1px solid var(--ld-border, #ececec);
	background: var(--ld-bg-card, #fff);
	font-size: 11px;
	font-weight: 600;
	color: var(--ld-text-secondary, #888);
	display: inline-flex;
	align-items: center;

	&.is-on {
		background: var(--ld-accent-muted, #eef0ff);
		color: var(--ld-accent-muted-text, #5347e0);
		border-color: transparent;
	}
}

.lv-payoff,
.lv-spark {
	display: block;
	width: 100%;
	flex: 1;
	margin-top: 8px;
	min-height: 72px;
}

.lv-axis {
	stroke: var(--ld-border-strong, #d0d0d0);
	stroke-width: 1;
	stroke-dasharray: 3 3;
}

.lv-payoff-fill,
.lv-spark-fill {
	fill: var(--ld-accent-muted, #eef0ff);
	animation: lv-fade-in 0.9s ease 0.15s both;
}

.lv-payoff-line,
.lv-spark-line {
	fill: none;
	stroke: var(--ld-accent, #5347e0);
	stroke-width: 2.2;
	stroke-linecap: round;
	stroke-linejoin: round;
	stroke-dasharray: 1;
	stroke-dashoffset: 1;
	animation: lv-draw 1.1s ease 0.12s forwards;
}

.lv-dot {
	fill: var(--ld-accent, #5347e0);
	animation: lv-dot 1.8s ease-in-out 1s infinite;
}

.lv-meta {
	display: flex;
	justify-content: space-between;
	margin-top: 8px;
	font-size: 12px;
	font-weight: 700;
}

.lv-mini-actions {
	margin-top: 8px;

	.lv-side-btn {
		height: 30px;
		font-size: 11px;
	}
}

.lv-list {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 6px;
}

.lv-list-row {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(56px, 72px) auto;
	align-items: center;
	gap: 10px;
	min-height: 36px;
	padding: 6px 8px;
	border-radius: 10px;
	background: var(--ld-bg-card, #fff);
	border: 1px solid transparent;
	animation: lv-row-in 0.46s ease both;
	animation-delay: calc(var(--lv-i, 0) * 80ms);
}

.lv-asset {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.lv-mark {
	width: 26px;
	height: 26px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	font-weight: 700;
	background: var(--ld-accent-muted, #eef0ff);
	color: var(--ld-accent, #5347e0);
	flex-shrink: 0;
}

.lv-mark.t-b {
	background: color-mix(in srgb, var(--ld-success, #16a34a) 14%, transparent);
	color: var(--ld-success, #16a34a);
}

.lv-mark.t-c {
	background: var(--ld-bg-active, #f2f2f2);
	color: var(--ld-text, #111);
}

.lv-sym {
	font-size: 12px;
	font-weight: 700;
	letter-spacing: -0.02em;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.lv-mini {
	width: 100%;
	height: 22px;
	overflow: visible;
}

.lv-mini-fill {
	opacity: 0.16;

	&.is-up { fill: var(--ld-success, #16a34a); }
	&.is-down { fill: var(--ld-danger, #ef4444); }
}

.lv-mini-line {
	fill: none;
	stroke-width: 1.7;
	stroke-linecap: round;
	stroke-linejoin: round;
	stroke-dasharray: 1;
	stroke-dashoffset: 1;
	animation: lv-draw 0.9s ease calc(0.12s + var(--lv-i, 0) * 80ms) forwards;

	&.is-up { stroke: var(--ld-success, #16a34a); }
	&.is-down { stroke: var(--ld-danger, #ef4444); }
}

.lv-quote {
	text-align: right;
	font-variant-numeric: tabular-nums;
	min-width: 58px;
}

.lv-px,
.lv-quote > span:first-child {
	display: block;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: -0.02em;
	line-height: 1.2;
	color: var(--ld-text, #111);
}

.lv-chg,
.lv-quote > span:last-child {
	display: inline-block;
	margin-top: 2px;
	font-size: 10px;
	font-weight: 700;
	line-height: 1.2;
	padding: 1px 5px;
	border-radius: 999px;
}

.lv-chg.is-up,
.lv-quote > span.is-up {
	background: color-mix(in srgb, var(--ld-success, #16a34a) 14%, transparent);
}

.lv-chg.is-down,
.lv-quote > span.is-down {
	background: color-mix(in srgb, var(--ld-danger, #ef4444) 14%, transparent);
}

.lv-chg {
	animation: lv-chg-pulse 2.8s ease-in-out calc(1.1s + var(--lv-i, 0) * 0.35s) infinite;
}

.lv-lend {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 14px;
}

.lv-lend-top {
	display: flex;
	justify-content: space-between;
	font-size: 12px;
	font-weight: 700;
	margin-bottom: 6px;
}

.lv-bar {
	height: 6px;
	border-radius: 999px;
	background: var(--ld-bg-card, #fff);
	border: 1px solid var(--ld-border, #ececec);
	overflow: hidden;

	i {
		display: block;
		height: 100%;
		background: var(--ld-accent, #5347e0);
		border-radius: inherit;
		transform-origin: left center;
		animation: lv-bar 0.8s ease both;
	}
}

.lv-sec {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 12px;
	padding: 22px 20px;
}

.lv-sec-mark {
	width: 48px;
	height: 48px;
	border-radius: 14px;
	background: var(--ld-accent-muted, #eef0ff);
	color: var(--ld-accent, #5347e0);
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 4px;
}

.lv-sec-row {
	display: flex;
	align-items: center;
	gap: 10px;
	font-size: 14px;
	font-weight: 600;
}

.lv-check {
	width: 22px;
	height: 22px;
	border-radius: 50%;
	background: var(--ld-accent-muted, #eef0ff);
	color: var(--ld-accent, #5347e0);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.is-up {
	color: var(--ld-success, #16a34a);
}

.is-down {
	color: var(--ld-danger, #ef4444);
}

html[data-lending-theme='dark'] {
	.lv-glow-a { opacity: 0.34; }
	.lv-glow-b { opacity: 0.2; }

	.lv-window {
		box-shadow: none;
	}

	.lv-area,
	.lv-payoff-fill,
	.lv-spark-fill {
		fill: var(--ld-accent-muted, #221f48);
	}

	.lv-list-row {
		background: var(--ld-bg-elevated, #1a1919);
	}
}

.lv.is-compact {
	.lv-hero-body {
		grid-template-columns: minmax(0, 1fr);
	}

	.lv-book,
	.lv-chrome-tabs {
		display: none;
	}

	.lv-candles {
		min-height: 120px;
	}
}

@keyframes lv-draw {
	to { stroke-dashoffset: 0; }
}

@keyframes lv-fade-in {
	from { opacity: 0; }
	to { opacity: 1; }
}

@keyframes lv-row-in {
	from {
		opacity: 0;
		transform: translateY(6px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes lv-candle {
	from {
		opacity: 0;
		transform: scaleY(0.35);
	}
	to {
		opacity: 1;
		transform: scaleY(1);
	}
}

@keyframes lv-bar {
	from { transform: scaleX(0.18); }
	to { transform: scaleX(1); }
}

@keyframes lv-glow {
	0%, 100% { transform: scale(1); }
	50% { transform: scale(1.12); }
}

@keyframes lv-dot {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.35; }
}

@keyframes lv-chg-pulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.72; }
}

@media (prefers-reduced-motion: reduce) {
	.lv-glow-a,
	.lv-glow-b,
	.lv-area,
	.lv-candles g,
	.lv-book-row i,
	.lv-payoff-fill,
	.lv-spark-fill,
	.lv-payoff-line,
	.lv-spark-line,
	.lv-dot,
	.lv-list-row,
	.lv-mini-line,
	.lv-bar i,
	.lv-chg {
		animation: none !important;
		stroke-dashoffset: 0;
		transform: none;
		opacity: 1;
	}
}
</style>
